import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../../generic/smartSelect'
import AlertUI from '../../generic/AlertUI'

const variables = ['x', 'y', 'z', 'a', 'b', 'c']

const emptyVariableValues = () => {
  const values = {}
  variables.forEach((variable) => {
    values[variable] = { value: '', upperTolerance: '', lowerTolerance: '' }
  })
  return values
}

const PartReferencePresetForm = forwardRef(({ partReferenceId, onClose, onSaved }, ref) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [fixtureReferences, setFixtureReferences] = useState([])
  const [selectedFixtureCode, setSelectedFixtureCode] = useState('')
  const [variableValues, setVariableValues] = useState(emptyVariableValues())
  const [presetNumber, setPresetNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // numeroPreset (machine preset number, stored outside our context) is mutually exclusive with
  // the tolerance values: if a preset number is set, the normal values must not be used.
  const usePresetNumber = presetNumber !== ''
  const matrixHasValues = variables.some((variable) => {
    const { value, upperTolerance, lowerTolerance } = variableValues[variable]
    return value !== '' || upperTolerance !== '' || lowerTolerance !== ''
  })

  useEffect(() => {
    const fetchFixtureReferences = async () => {
      const references = await apiGet('fixtureReferencesByPartReference/' + partReferenceId)
      setFixtureReferences(references || [])
    }
    fetchFixtureReferences()
  }, [partReferenceId])

  const fixtureOptions = useMemo(() => fixtureReferences.map((reference) => reference.code), [fixtureReferences])

  const handleVariableChange = (variable, field, value) => {
    setVariableValues((previous) => ({
      ...previous,
      [variable]: { ...previous[variable], [field]: value }
    }))
  }

  const validateTolerance = (variable, upperCheckMode) => {
    const { value, upperTolerance, lowerTolerance } = variableValues[variable]
    const current = upperCheckMode ? upperTolerance : lowerTolerance

    if (value === '') {
      // Value is not defined so upper and lower must be empty too
      if (current === '') return ''
      return t_('Should be empty')
    }

    const currentNum = parseFloat(current)
    if (isNaN(currentNum)) {
      return t_('Is not a number')
    }

    const upperNum = parseFloat(upperTolerance)
    const lowerNum = parseFloat(lowerTolerance)
    if (isNaN(upperNum) || isNaN(lowerNum)) return ''
    if (upperNum <= lowerNum) {
      return t_('Upper tolerance must be higher than lower tolerance')
    }
    return ''
  }

  const buildPayload = () => {
    const selectedFixture = fixtureReferences.find((reference) => reference.code === selectedFixtureCode)
    const payload = {
      partReference: partReferenceId,
      fixtureReference: selectedFixture,
      presetNumber: usePresetNumber ? presetNumber : null
    }
    variables.forEach((variable) => {
      const { value, upperTolerance, lowerTolerance } = variableValues[variable]
      payload[variable + 'value'] = usePresetNumber || value === '' ? null : value
      payload[variable + 'valueUpperTolerance'] = usePresetNumber || upperTolerance === '' ? null : upperTolerance
      payload[variable + 'valueLowerTolerance'] = usePresetNumber || lowerTolerance === '' ? null : lowerTolerance
    })
    return payload
  }

  const handleSave = async () => {
    if (isSubmitting) return
    if (!selectedFixtureCode) {
      showAlert({ severity: 'error', title: t_('Fixture Reference') + ': ' + t_('This field is mandatory') })
      return
    }
    if (!usePresetNumber) {
      for (const variable of variables) {
        const upperError = validateTolerance(variable, true)
        const lowerError = validateTolerance(variable, false)
        if (upperError || lowerError) {
          showAlert({ severity: 'error', title: variable.toUpperCase() + ': ' + (upperError || lowerError) })
          return
        }
      }
    }

    try {
      setIsSubmitting(true)
      await apiPost('fixtureReferencePartReferencePreset', buildPayload())
      if (onSaved && typeof onSaved === 'function') onSaved()
      if (onClose && typeof onClose === 'function') onClose()
    } catch (error) {
      showAlert({ severity: 'error', title: error.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  useImperativeHandle(ref, () => ({ submit: handleSave }))

  return (
    <Box sx={{ minWidth: '600px', padding: '1rem' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 9999 }}>{AlertElement}</div>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <SmartSelect
          label={t_('Fixture Reference')}
          value={selectedFixtureCode}
          selectableOptions={fixtureOptions}
          onChange={(value) => setSelectedFixtureCode(value || '')}
          autoSelectFirst={false}
          required
          width='100%'
        />
      </Box>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <TextField
          type='number'
          size='small'
          fullWidth
          label={t_('Preset number')}
          value={presetNumber}
          onChange={(event) => setPresetNumber(event.target.value)}
          disabled={matrixHasValues}
          helperText={t_('If set, the machine preset is used and tolerance values are ignored')}
        />
      </Box>
      <Typography variant='subtitle2' color='primary' sx={{ marginBottom: '0.5rem' }}>
        {t_('Tolerance data matrix')}
      </Typography>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align='center'>{t_('Value')}</TableCell>
            <TableCell align='center'>{t_('Upper tolerance')}</TableCell>
            <TableCell align='center'>{t_('Lower tolerance')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {variables.map((variable) => {
            const upperError = validateTolerance(variable, true)
            const lowerError = validateTolerance(variable, false)
            return (
              <TableRow key={variable}>
                <TableCell sx={{ verticalAlign: 'top', paddingTop: '1.25rem' }}>
                  <Typography variant='body2'>{variable.toUpperCase()}</Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    type='number'
                    size='small'
                    disabled={usePresetNumber}
                    value={variableValues[variable].value}
                    onChange={(event) => handleVariableChange(variable, 'value', event.target.value)}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    type='number'
                    size='small'
                    disabled={usePresetNumber}
                    value={variableValues[variable].upperTolerance}
                    onChange={(event) => handleVariableChange(variable, 'upperTolerance', event.target.value)}
                    error={!!upperError}
                    helperText={upperError}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    type='number'
                    size='small'
                    disabled={usePresetNumber}
                    value={variableValues[variable].lowerTolerance}
                    onChange={(event) => handleVariableChange(variable, 'lowerTolerance', event.target.value)}
                    error={!!lowerError}
                    helperText={lowerError}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
})

PartReferencePresetForm.displayName = 'PartReferencePresetForm'

export default PartReferencePresetForm
